import parseUserAgent from 'ua-parser-js';
import { parallel } from 'async';

import {
  Form,
  ChoiceList,
  ClassificationSet,
  Record,
  User,
  Project,
  PhotoItemValue,
  AudioItemValue,
  VideoItemValue,
  SketchItemValue,
  Changeset,
  MediaItemValue,
} from '@fulcrumapp/fulcrum-core';
import request from '../api/browser';
import Client from '../api/client';
import { getFullAttachmentInfo } from '../api/attachments-actions/attachments-info';

const invoke = (promise, callback) => promise
  .then((...args) => callback(null, ...args))
  .catch((...args) => callback(...args));

export default class ApiDataSource {
  client: Client;

  callbacks: {[key: string]: Function[]};

  request: any;

  constructor(token, base, config) {
    this.callbacks = {};

    this.request = request;

    this.client = new Client({
      token, base, request, config,
    });
  }

  checkAlreadyFetching(id, callback) {
    if (!this.callbacks[id]) {
      this.callbacks[id] = [];
    }

    this.callbacks[id].push(callback);

    return this.callbacks[id].length > 1;
  }

  invokeCallbacks(id, err, object) {
    for (const handler of this.callbacks[id]) {
      handler(err, object);
    }

    delete this.callbacks[id];
  }

  getChoiceList(id, callback) {
    if (this.checkAlreadyFetching(id, callback)) {
      return;
    }
    // @ts-ignore
    invoke(this.client.choiceLists.find(id), (err, json) => {
      this.invokeCallbacks(id, err, new ChoiceList(json));
    });
  }

  getClassificationSet(id, callback) {
    if (this.checkAlreadyFetching(id, callback)) {
      return;
    }
    // @ts-ignore
    invoke(this.client.classificationSets.find(id), (err, json) => {
      this.invokeCallbacks(id, err, new ClassificationSet(json));
    });
  }

  getForm(id, callback) {
    if (this.checkAlreadyFetching(id, callback)) {
      return;
    }
    // @ts-ignore
    invoke(this.client.forms.find(id), (err, json) => {
      this.invokeCallbacks(id, err, new Form(json));
    });
  }

  getRecord(id, form, callback) {
    if (this.checkAlreadyFetching(id, callback)) {
      return;
    }
    // @ts-ignore
    invoke(this.client.records.find(id), (err, json) => {
      this.invokeCallbacks(id, err, new Record(json, form));
    });
  }

  getRecords(form, requestParams, callback) {
    const params = requestParams || {};

    params.form_id = form.id;
    // @ts-ignore
    invoke(this.client.records.all(params), (err, page) => {
      if (err) {
        return callback(err);
      }

      const records = page.objects.map((json) => new Record(json, form));

      return callback(null, records, page.totalCount);
    });
  }

  queryAST(params, callback) {
    invoke(this.client.query.ast(params), (err, ast) => {
      if (err) {
        return callback(err);
      }

      return callback(null, ast);
    });
  }

  query(params, callback) {
    invoke(this.client.query.run(params), (err, page) => {
      if (err) {
        return callback(err);
      }

      return callback(null, page.objects, page);
    });
  }

  queryRecords(form, requestParams, callback) {
    const params = requestParams || {};
    params.form_id = form.id;

    invoke(this.client.records.query(params), (err, page) => {
      if (err) {
        return callback(err);
      }

      const records = page.objects.map((json) => new Record(json, form));

      return callback(null, records, page.totalCount);
    });
  }

  getUsers(params, callback) {
    const users: User[] = [];

    const addUser = (json) => {
      users.push(
        new User({
          id: json.user_id,
          email: json.email,
          name: json.user,
        }),
      );
    };
    // @ts-ignore
    invoke(this.client.memberships.all({ page: 1 }), (err, firstPage) => {
      if (err) {
        callback(err);
        return;
      }

      firstPage.objects.forEach(addUser);

      if (firstPage.totalPages > 1) {
        const tasks = [];

        for (let i = 2; i <= firstPage.totalPages; i++) {
          // @ts-ignore
          tasks.push((callback) => {
            // @ts-ignore
            invoke(this.client.memberships.all({ page: i }), callback);
          });
        }

        parallel(tasks, (error, results) => {
          if (err) {
            return callback(err);
          }

          results.forEach((page) => {
            page.objects.forEach(addUser);
          });

          return callback(null, users);
        });
      } else {
        callback(null, users);
      }
    });
  }

  getProjects(params, callback) {
    const projects: Project[] = [];

    const addProject = (json) => {
      projects.push(
        new Project(json),
      );
    };
    // @ts-ignore
    invoke(this.client.projects.all({ page: 1, per_page: 500 }), (err, firstPage) => {
      if (err) {
        callback(err);
        return;
      }

      firstPage.objects.forEach(addProject);

      if (firstPage.totalPages > 1) {
        const tasks: ((cb: any) => void)[] = [];

        for (let i = 2; i <= firstPage.totalPages; i++) {
          tasks.push((callback) => {
            // @ts-ignore
            invoke(this.client.projects.all({ page: i, per_page: 500 }), callback);
          });
        }

        parallel(tasks, (error, results) => {
          if (error) {
            return callback(error);
          }

          results.forEach((page) => {
            page.objects.forEach(addProject);
          });

          return callback(null, projects);
        });
      } else {
        callback(null, projects);
      }
    });
  }

  getChangeset(id, callback) {
    if (this.checkAlreadyFetching(id, callback)) {
      return;
    }
    // @ts-ignore
    invoke(this.client.changesets.find(id), (err, json) => {
      this.invokeCallbacks(id, err, new Changeset(json));
    });
  }

  getPhoto(id, callback) {
    // @ts-ignore
    invoke(this.client.photos.find(id, { recognition: true }), callback);
  }

  getSketch(id, callback) {
    // @ts-ignore
    invoke(this.client.sketches.find(id), callback);
  }

  getAttachment(id, callback) {
    const attachmentsUrl = this.client.config?.attachments_url;

    if (!attachmentsUrl) {
      callback(new Error('attachments_url not configured'), null);
      return;
    }

    const attachmentUrl = `${attachmentsUrl}/${id}`;

    getFullAttachmentInfo(attachmentUrl, this.client.token)
      .then((result) => callback(null, result))
      .catch((err) => callback(err, null));
  }

  getVideo(id, callback) {
    // @ts-ignore
    invoke(this.client.videos.find(id), callback);
  }

  getVideoURL(id) {
    return this.client.urlFromPath(this.client.videos.videoURL(id));
  }

  getVideoTrack(id, callback) {
    invoke(this.client.videos.track(id), (err, track) => {
      if (err) {
        throw err;
      }

      return callback(null, track);
    });
  }

  getVideoTrackURL(id, format) {
    return this.client.urlFromPath(this.client.videos.trackURL(id, format));
  }

  getAudio(id, callback) {
    // @ts-ignore
    invoke(this.client.audio.find(id), callback);
  }

  getAudioURL(id) {
    return this.client.urlFromPath(this.client.audio.audioURL(id));
  }

  getAudioTrack(id, callback) {
    // @ts-ignore
    invoke(this.client.audio.track(id), (err, body) => {
      if (err) {
        throw err;
      }

      return callback(null, JSON.parse(body));
    });
  }

  getAudioTrackURL(id, format) {
    return this.client.urlFromPath(this.client.audio.trackURL(id, format));
  }

  getPhotoThumbnailURL(id: string) {
    return this.client.photos.thumbnailURL(id);
  }

  getPhotoPreviewURL(id: string) {
    return this.client.photos.largeURL(id);
  }

  getVideoPreviewURL(id: string) {
    return this.client.videos.largeURL(id);
  }

  getVideoThumbnailURL(id: string) {
    return this.client.videos.thumbnailURL(id);
  }

  getSketchThumbnailURL(id: string) {
    return this.client.sketches.thumbnailURL(id);
  }

  getSketchPreviewURL(id: string) {
    return this.client.sketches.largeURL(id);
  }

  getSignatureThumbnailURL(id: string) {
    return this.client.signatures.thumbnailURL(id);
  }

  getSignaturePreviewURL(id: string) {
    return this.client.signatures.largeURL(id);
  }

  getThumbnailURL(item: MediaItemValue) {
    if (item instanceof PhotoItemValue) {
      return this.client.photos.thumbnailURL(item.mediaID);
    }

    if (item instanceof VideoItemValue) {
      return this.client.videos.thumbnailURL(item.mediaID);
    }

    if (item instanceof AudioItemValue) {
      return this.client.audio.thumbnailURL(item.mediaID);
    }

    if (item instanceof SketchItemValue) {
      return this.client.sketches.thumbnailURL(item.mediaID);
    }

    return null;
  }

  createSignature(accessKey, file, progress) {
    return this.client.signatures.create(file, { access_key: accessKey }, progress);
  }

  createPhoto(accessKey, file, progress) {
    return this.client.photos.create(file, { access_key: accessKey }, progress);
  }

  createSketch(accessKey, file, progress) {
    return this.client.sketches.create(file, { access_key: accessKey }, progress);
  }

  createAttachment(accessKey, file, progress) {
    return this.client.attachments.create(file, { access_key: accessKey }, progress);
  }

  createVideo(accessKey, file, progress) {
    return this.client.videos.create(file, { access_key: accessKey }, progress);
  }

  createAudio(accessKey, file, progress) {
    return this.client.audio.create(file, { access_key: accessKey }, progress);
  }

  saveVideoTrack(accessKey, file, progress) {
    return this.client.videos.uploadTrack(file, { access_key: accessKey }, progress);
  }

  saveAudioTrack(accessKey, file, progress) {
    return this.client.audio.uploadTrack(file, { access_key: accessKey }, progress);
  }

  getFormMembers(formId, callback) {
    invoke(this.client.permissions.formMembers(formId), callback);
  }

  buildChangesetAttributes(record) {
    const recordJSON = record.toJSON();

    const ua = parseUserAgent();

    const changesetAttributes = {
      form_id: recordJSON.form_id,
      metadata: {
        application: 'Fulcrum Web',
        browser: ua.browser.name,
        browser_version: ua.browser.version,
        platform: ua.os.name,
        platform_version: ua.os.version,
        user_agent: ua.ua,
      },
    };

    if (ua.device.model) {
      // @ts-ignore
      changesetAttributes.metadata.device_model = ua.device.model;
    }

    if (ua.device.vendor) {
      // @ts-ignore
      changesetAttributes.metadata.device_manufacturer = ua.device.vendor;
    }

    return changesetAttributes;
  }

  saveRecord(record, callback) {
    const changesetAttributes = this.buildChangesetAttributes(record);
    // @ts-ignore
    invoke(this.client.changesets.create(changesetAttributes), (err, changesetJSON) => {
      const changeset = new Changeset(changesetJSON);

      if (err) {
        callback(err);
        return;
      }

      record.changeset = changeset;
      // @ts-ignore
      invoke(this.client.records.create(record.toJSON()), (err, object) => {
        if (err) {
          callback(err);
          return;
        }

        record.updateFromAPIAttributes(object);

        invoke(this.client.changesets.close(changeset.id), (err, cs) => {
          if (err) {
            return callback(err);
          }

          return callback(null, record);
        });
      });
    });
  }

  deleteRecord(record, callback) {
    const changesetAttributes = this.buildChangesetAttributes(record);
    // @ts-ignore
    invoke(this.client.changesets.create(changesetAttributes), (err, changesetJSON) => {
      const changeset = new Changeset(changesetJSON);

      if (err) {
        return callback(err);
      }

      invoke(this.client.records.delete(record.id, changeset.id), (err, object) => {
        if (err) {
          return callback(err);
        }

        invoke(this.client.changesets.close(changeset.id), (err, cs) => {
          if (err) {
            return callback(err);
          }

          return callback(null, record);
        });

        return null;
      });

      return null;
    });
  }

  createExport(exportAttributes, callback) {
    // @ts-ignore
    invoke(this.client.exports.create(exportAttributes), callback);
  }

  getExport(id, callback) {
    // @ts-ignore
    invoke(this.client.exports.find(id), callback);
  }

  cancelExport(id, callback) {
    // @ts-ignore
    invoke(this.client.exports.delete(id), callback);
  }

  createView(attrs, callback) {
    // @ts-ignore
    invoke(this.client.views.create(attrs), callback);
  }

  updateView(id, attrs, callback) {
    // @ts-ignore
    invoke(this.client.views.update(id, attrs), callback);
  }

  deleteView(id, callback) {
    // @ts-ignore
    invoke(this.client.views.delete(id), callback);
  }

  getView(id, callback) {
    // @ts-ignore
    invoke(this.client.views.find(id), callback);
  }
}
